import { useState, useEffect } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  BarChart3, TrendingUp, TrendingDown, Clock, Users, Activity,
  AlertTriangle, Heart, Hospital, Eye, RefreshCw, Calendar
} from "lucide-react";

// Simple line chart component using SVG
function SimpleLineChart({ data, dataKey, color = "#0ea5e9", height = 120 }) {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const min = 0;
  const range = max - min || 1;
  
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1 || 1)) * 100;
    const y = 100 - ((v - min) / range) * 100;
    return `${x},${y}`;
  }).join(" ");
  
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }} preserveAspectRatio="none">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
      {values.map((v, i) => {
        const x = (i / (values.length - 1 || 1)) * 100;
        const y = 100 - ((v - min) / range) * 100;
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r="3"
            fill={color}
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
    </svg>
  );
}

// Multi-line chart for symptoms
function SymptomChart({ data, height = 200 }) {
  if (!data || data.length === 0) return (
    <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
      Pas de données pour cette période
    </div>
  );
  
  const symptoms = ["fievre", "dyspnee", "douleur", "nausee", "fatigue"];
  const colors = {
    fievre: "#ef4444",
    dyspnee: "#f97316", 
    douleur: "#eab308",
    nausee: "#22c55e",
    fatigue: "#8b5cf6"
  };
  const labels = {
    fievre: "Fièvre",
    dyspnee: "Dyspnée",
    douleur: "Douleur",
    nausee: "Nausée",
    fatigue: "Fatigue"
  };
  
  const allValues = data.flatMap(d => symptoms.map(s => d[s] || 0));
  const max = Math.max(...allValues, 1);
  
  return (
    <div>
      <svg viewBox="0 0 100 60" className="w-full" style={{ height }} preserveAspectRatio="none">
        {symptoms.map(symptom => {
          const values = data.map(d => d[symptom] || 0);
          const points = values.map((v, i) => {
            const x = (i / (values.length - 1 || 1)) * 100;
            const y = 60 - (v / max) * 55;
            return `${x},${y}`;
          }).join(" ");
          
          return (
            <polyline
              key={symptom}
              points={points}
              fill="none"
              stroke={colors[symptom]}
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
              opacity="0.8"
            />
          );
        })}
      </svg>
      
      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-3 justify-center">
        {symptoms.map(s => (
          <div key={s} className="flex items-center gap-1.5 text-xs">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[s] }} />
            <span className="text-slate-600">{labels[s]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ClinicAnalytics({ clinicId, clinicName }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/kpi/clinic/${clinicId}/analytics?period=${period}`);
      setAnalytics(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des analytics");
      // error handled by toast
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clinicId) {
      fetchAnalytics();
    }
  }, [clinicId, period]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12 text-slate-400">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p>Données analytiques non disponibles</p>
      </div>
    );
  }

  const { overview, idePerformance, resolutionOutcomes, symptomTrends, weekComparison } = analytics;

  // Calculate trend indicators
  const TrendIndicator = ({ value, inverted = false }) => {
    if (value === null || value === undefined) return null;
    const isPositive = inverted ? value < 0 : value > 0;
    const Icon = value > 0 ? TrendingUp : TrendingDown;
    return (
      <span className={`flex items-center gap-1 text-xs ${isPositive ? "text-green-600" : "text-red-600"}`}>
        <Icon className="w-3 h-3" />
        {Math.abs(value)}%
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="clinic-analytics">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
            Dashboard Analytique
          </h2>
          {clinicName && <p className="text-sm text-slate-500">{clinicName}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <select
            value={period}
            onChange={e => setPeriod(Number(e.target.value))}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white"
            data-testid="period-selector"
          >
            <option value={7}>7 derniers jours</option>
            <option value={30}>30 derniers jours</option>
            <option value={90}>90 derniers jours</option>
          </select>
          <button
            onClick={fetchAnalytics}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            title="Actualiser"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Overview KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-50 rounded-lg">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{overview.totalPatients}</p>
              <p className="text-xs text-slate-500">Patients suivis</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-50 rounded-lg">
              <Activity className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{overview.totalAlerts}</p>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                Alertes générées
                <TrendIndicator value={overview.alertsTrend} inverted />
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-50 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{overview.criticalAlerts}</p>
              <p className="text-xs text-slate-500">Alertes critiques</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-orange-50 rounded-lg">
              <Eye className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">{overview.orangeAlerts}</p>
              <p className="text-xs text-slate-500">Alertes orange</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* IDE Performance */}
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-green-600" />
            Temps de réponse IDE
          </h3>
          <div className="text-center py-4">
            <p className="text-4xl font-bold text-green-600">
              {idePerformance.avgResponseMinutes !== null 
                ? `${idePerformance.avgResponseMinutes} min`
                : "—"
              }
            </p>
            <p className="text-sm text-slate-500 mt-1">Temps moyen de prise en charge</p>
            {idePerformance.trend !== null && (
              <div className="mt-3 flex items-center justify-center gap-2">
                <span className={`text-sm font-medium ${idePerformance.trend < 0 ? "text-green-600" : "text-red-600"}`}>
                  {idePerformance.trend > 0 ? "+" : ""}{idePerformance.trend} min
                </span>
                <span className="text-xs text-slate-400">vs période précédente</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-lg font-semibold text-slate-700">{idePerformance.totalResponses}</p>
              <p className="text-xs text-slate-500">Alertes traitées</p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-lg font-semibold text-slate-700">
                {idePerformance.prevAvgResponseMinutes ?? "—"} min
              </p>
              <p className="text-xs text-slate-500">Période précédente</p>
            </div>
          </div>
        </div>

        {/* Resolution Outcomes */}
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Hospital className="w-5 h-5 text-purple-600" />
            Issues des alertes résolues
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full" />
                <span className="text-sm text-slate-700">Hospitalisation nécessaire</span>
              </div>
              <span className="text-lg font-bold text-red-600">{resolutionOutcomes.hospitalisationsNecessaires}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full" />
                <span className="text-sm text-slate-700">Hospitalisation évitée</span>
              </div>
              <span className="text-lg font-bold text-green-600">{resolutionOutcomes.hospitalisationsEvitees}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full" />
                <span className="text-sm text-slate-700">Simple surveillance</span>
              </div>
              <span className="text-lg font-bold text-blue-600">{resolutionOutcomes.simpleSurveillance}</span>
            </div>
          </div>
          {resolutionOutcomes.total > 0 && (
            <div className="mt-4 pt-4 border-t text-center">
              <p className="text-sm text-slate-500">
                <span className="font-semibold text-green-600">
                  {Math.round((resolutionOutcomes.hospitalisationsEvitees / resolutionOutcomes.total) * 100)}%
                </span>
                {" "}d'hospitalisations évitées grâce au télésuivi
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Symptom Trends Chart */}
      <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Heart className="w-5 h-5 text-pink-600" />
          Tendances des symptômes
        </h3>
        <SymptomChart data={symptomTrends.chart} height={180} />
        
        {/* Symptom Totals */}
        {symptomTrends.totals && Object.keys(symptomTrends.totals).length > 0 && (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-6 pt-4 border-t">
            {Object.entries(symptomTrends.totals).map(([symptom, count]) => (
              <div key={symptom} className="text-center p-2 bg-slate-50 rounded-lg">
                <p className="text-lg font-semibold text-slate-700">{count}</p>
                <p className="text-xs text-slate-500 capitalize">{symptom}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Week Comparison */}
      <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-indigo-600" />
          Comparaison hebdomadaire
        </h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-slate-500 mb-3">Check-ins</p>
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <div className="h-24 bg-slate-100 rounded-lg relative overflow-hidden">
                  <div 
                    className="absolute bottom-0 left-0 right-0 bg-emerald-500 rounded-b-lg transition-all"
                    style={{ 
                      height: `${Math.min(100, (weekComparison.currentWeek.checkins / Math.max(weekComparison.previousWeek.checkins, weekComparison.currentWeek.checkins, 1)) * 100)}%` 
                    }}
                  />
                </div>
                <p className="text-center mt-2 text-sm font-medium">{weekComparison.currentWeek.checkins}</p>
                <p className="text-center text-xs text-slate-500">Cette semaine</p>
              </div>
              <div className="flex-1">
                <div className="h-24 bg-slate-100 rounded-lg relative overflow-hidden">
                  <div 
                    className="absolute bottom-0 left-0 right-0 bg-slate-400 rounded-b-lg transition-all"
                    style={{ 
                      height: `${Math.min(100, (weekComparison.previousWeek.checkins / Math.max(weekComparison.previousWeek.checkins, weekComparison.currentWeek.checkins, 1)) * 100)}%` 
                    }}
                  />
                </div>
                <p className="text-center mt-2 text-sm font-medium">{weekComparison.previousWeek.checkins}</p>
                <p className="text-center text-xs text-slate-500">Semaine préc.</p>
              </div>
            </div>
            <div className="text-center mt-3">
              <TrendIndicator value={weekComparison.checkinsTrend} />
            </div>
          </div>
          
          <div>
            <p className="text-sm text-slate-500 mb-3">Alertes</p>
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <div className="h-24 bg-slate-100 rounded-lg relative overflow-hidden">
                  <div 
                    className="absolute bottom-0 left-0 right-0 bg-amber-500 rounded-b-lg transition-all"
                    style={{ 
                      height: `${Math.min(100, (weekComparison.currentWeek.alerts / Math.max(weekComparison.previousWeek.alerts, weekComparison.currentWeek.alerts, 1)) * 100)}%` 
                    }}
                  />
                </div>
                <p className="text-center mt-2 text-sm font-medium">{weekComparison.currentWeek.alerts}</p>
                <p className="text-center text-xs text-slate-500">Cette semaine</p>
              </div>
              <div className="flex-1">
                <div className="h-24 bg-slate-100 rounded-lg relative overflow-hidden">
                  <div 
                    className="absolute bottom-0 left-0 right-0 bg-slate-400 rounded-b-lg transition-all"
                    style={{ 
                      height: `${Math.min(100, (weekComparison.previousWeek.alerts / Math.max(weekComparison.previousWeek.alerts, weekComparison.currentWeek.alerts, 1)) * 100)}%` 
                    }}
                  />
                </div>
                <p className="text-center mt-2 text-sm font-medium">{weekComparison.previousWeek.alerts}</p>
                <p className="text-center text-xs text-slate-500">Semaine préc.</p>
              </div>
            </div>
            <div className="text-center mt-3">
              <TrendIndicator value={weekComparison.alertsTrend} inverted />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
